/**
 * LinkRequestsPanel — DEC-346 (a) / OPEN-131. People an import asked to join this company
 * because they already have a lodeX Pro login at another company.
 *
 * The person answers in their own app; nothing here can accept for them, and the copy
 * says so. The admin can withdraw an open request, or ask again once one has expired
 * (D5: 14 days). Declined and cancelled requests stay listed so the admin can see the
 * answer, but they offer no action: re-asking someone who said no is a new import.
 */

import { apiErrorText, roleDisplayLabel } from '@lodex-pro/web-shared';
import { toast } from 'sonner';
import {
  useLinkRequests,
  useCancelLinkRequest,
  useResendLinkRequest,
  type LinkRequest,
  type LinkRequestStatus,
} from './link-requests';

const STATUS_TEXT: Record<LinkRequestStatus, string> = {
  PENDING: 'Waiting for them to accept',
  ACCEPTED: 'Accepted — on your team',
  DECLINED: 'Declined',
  CANCELLED: 'Withdrawn',
  EXPIRED: 'Expired — not answered',
};

function day(iso: string | null): string {
  if (iso === null) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

function fail(e: unknown): void {
  toast.error(apiErrorText(e, 'That did not work. Try again.'));
}

export function LinkRequestRow({ request }: { request: LinkRequest }): React.ReactElement {
  const cancel = useCancelLinkRequest(request.id);
  const resend = useResendLinkRequest(request.id);
  const busy = cancel.isPending || resend.isPending;
  const role = roleDisplayLabel(request.role) ?? request.role;
  const when =
    request.status === 'PENDING'
      ? `asks until ${day(request.expiresAt)}`
      : request.decidedAt !== null
        ? day(request.decidedAt)
        : '';

  return (
    <div
      data-testid="link-request-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 0',
        borderTop: '1px solid var(--color-border-tertiary)',
        fontSize: 13,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{request.fullName}</div>
        <div style={{ color: 'var(--color-text-secondary)' }}>
          {role}
          {request.phoneLast4 !== null ? ` · phone ending ${request.phoneLast4}` : ''}
        </div>
      </div>
      <div style={{ color: 'var(--color-text-secondary)', textAlign: 'right' }}>
        <div>{STATUS_TEXT[request.status]}</div>
        {when !== '' && <div style={{ fontSize: 12 }}>{when}</div>}
      </div>
      {request.status === 'PENDING' && (
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            if (!window.confirm(`Withdraw the request to ${request.fullName}?`)) return;
            cancel.mutate(
              {},
              {
                onSuccess: () => toast.success(`Request to ${request.fullName} withdrawn`),
                onError: fail,
              },
            );
          }}
          style={{ minHeight: 30 }}
        >
          {cancel.isPending ? '…' : 'Withdraw'}
        </button>
      )}
      {request.status === 'EXPIRED' && (
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            resend.mutate(
              {},
              {
                onSuccess: () =>
                  toast.success(
                    `${request.fullName} was asked again. They have 14 days to accept.`,
                  ),
                onError: fail,
              },
            )
          }
          style={{ minHeight: 30 }}
        >
          {resend.isPending ? '…' : 'Ask again'}
        </button>
      )}
    </div>
  );
}

export function LinkRequestsPanel(): React.ReactElement | null {
  const { data, isLoading, isError } = useLinkRequests();
  const requests = data?.requests ?? [];
  // Nothing to show for a company that has never asked anyone: no empty heading.
  if (!isLoading && !isError && requests.length === 0) return null;
  const open = requests.filter((r) => r.status === 'PENDING').length;

  return (
    <section style={{ marginTop: 24 }} aria-labelledby="link-requests-heading">
      <h2 id="link-requests-heading" style={{ fontSize: 15, fontWeight: 600 }}>
        Asked to join ({open} waiting)
      </h2>
      <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)', margin: '4px 0 8px' }}>
        These people already use lodeX Pro at another company. An import asked them to join yours;
        they accept or decline in their own app, and appear on your team only when they accept.
      </p>
      {isLoading && <p style={{ fontSize: 12 }}>Loading…</p>}
      {isError && (
        <p style={{ fontSize: 13, color: 'var(--color-danger)' }}>Could not load the requests.</p>
      )}
      {requests.map((r) => (
        <LinkRequestRow key={r.id} request={r} />
      ))}
    </section>
  );
}
